import { api } from '../config/api';
import { storeAuthData, validateAuthStorage } from '../utils/auth';
import React, { useState, useEffect } from 'react';
import './Login.css';

const Login: React.FC = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<React.ReactNode>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Validate email and password to enable/disable the button
  useEffect(() => {
    // Basic email validation regex
    const isEmailValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(username.toLowerCase());
    const isPasswordNotEmpty = password.length > 0;
    setIsButtonDisabled(!(isEmailValid && isPasswordNotEmpty));
  }, [username, password]);

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Prevent multiple submissions
    if (isLoading || isRedirecting) return;

    // Prevent submission if button is disabled due to validation
    if (isButtonDisabled) return;

    setIsLoading(true);
    setError(null);

    try {
      // Convert email to lowercase for case-insensitive comparison
      const emailLowercase = username.toLowerCase();
      if (process.env.NODE_ENV === "development") {
        console.log("SuperadminFE: Attempting login for:", emailLowercase);
      }

      // This calls our Spring Boot /login endpoint
      const response = await api.post('/login', {
        email: emailLowercase,
        password: password
      });

      if (process.env.NODE_ENV === "development") {
        console.log("SuperadminFE: Login response received:", response.status);
      }

      // Extract accessToken, refreshToken, userId, and role from response
      const { accessToken, refreshToken, userId, role } = response.data;

      if (!accessToken || !refreshToken || !userId || !role) {
        throw new Error("Invalid response: missing required authentication data");
      }

      if (process.env.NODE_ENV === "development") {
        console.log("SuperadminFE: Received authentication data:", {
          userId,
          role,
          accessTokenLength: accessToken.length,
          refreshTokenLength: refreshToken.length
        });
      }

      // Only SuperAdmin role is allowed to log in
      if (role !== "SuperAdmin") {
        if (role === "Operator") {
          setError(<>
            Permission Denied. Please try <a href="https://operator.parkwithparallel.com/login" target="_blank" rel="noopener noreferrer" style={{ color: '#ffbfbf' }}>Operator Portal</a>
          </>);
        } else {
          setError("Permission Denied");
        }
        setIsLoading(false);
        return;
      }

      // Store authentication data with both tokens
      const authData = storeAuthData(userId, accessToken, refreshToken, role);

      if (!authData) {
        throw new Error("Failed to store authentication data");
      }

      // Verify the auth data was properly stored
      const isValid = validateAuthStorage();

      if (!isValid) {
        throw new Error("Auth validation failed after storage");
      }

      if (process.env.NODE_ENV === "development") {
        console.log("SuperadminFE: Auth storage validated, preparing to navigate");
      }

      // Show redirecting state
      setIsRedirecting(true);

      // Redirect to dashboard with a slight delay for better UX
      setTimeout(() => {
        if (process.env.NODE_ENV === "development") {
          console.log("SuperadminFE: Performing redirect to dashboard...");
        }
        window.location.href = '/dashboard';
      }, 500);

    } catch (err: any) {
      console.error("SuperadminFE: Login error:", err);
      let errorMessage = "Invalid credentials";

      if (!err.response) {
        // Network error - server is likely offline
        errorMessage = "Server not responding. Please try again later.";
      } else if (err.response) {
        if (err.response.status === 401) {
          errorMessage = "Incorrect email or password";
        } else if (err.response.status === 403) {
          errorMessage = "Access denied. Please contact your administrator.";
        } else if (err.response.data) {
          errorMessage = err.response.data.message || err.response.data;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };

  if (isRedirecting) {
    return (
      <div className="login-container">
        <div className="login-box">
          <img src="/assets/LogotypeSuperAdminHorizontal.svg" alt="Logo" className="logo-superadmin" />
          <div className="redirecting-message">
            <p>Redirecting to dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-header">
        <img src="/assets/Logo_Parallel.svg" alt="Parallel Logo" className="parallel-logo" />
      </div>
      <div className="login-box">
        <img src="/assets/LogotypeSuperAdminHorizontal.svg" alt="Logo" className="logo-superadmin-inline" />

        <form onSubmit={handleLogin}>
          <div className="input-group">
            <input
              type="text"
              id="email"
              placeholder=" "
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              name="email"
              disabled={isLoading}
            />
            <label htmlFor="email">Email address</label>
          </div>
          <div className="input-group">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              placeholder=" "
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              name="password"
              disabled={isLoading}
            />
            <label htmlFor="password">Password</label>
            {password && (
              <button type="button" onClick={togglePasswordVisibility} className="password-toggle-button">
                <img
                  src={showPassword ? "/assets/HidePassword.svg" : "/assets/ShowPassword.svg"}
                  alt={showPassword ? "Hide password" : "Show password"}
                  width="24"
                  height="24"
                />
              </button>
            )}
          </div>
          <button
            type="submit"
            className={`login-button ${isLoading ? 'loading' : ''}${isButtonDisabled ? ' login-button-disabled' : ''}`}
            disabled={isLoading || isButtonDisabled}
          >
            {isLoading ? (
              'Logging in...'
            ) : (
              <>
                <img src="/assets/Login.svg" alt="Login" className="login-button-icon" width="18" height="18" />
                Login
              </>
            )}
          </button>
          {error && <p className="error"><img src="/assets/error.svg" alt="Error icon" width="16" height="16" />{error}</p>}
        </form>
      </div>
    </div>
  );
};

export default Login;
